import { useMemo } from 'react';
import type { SharingSettings } from '@/components/SharingSettingsSelector';

interface MediaUpload {
  type: 'photo' | 'video';
}

interface DishRating {
  rating: 'loved' | 'fine' | 'disliked' | null;
  mediaUrl?: string | null;
  mediaType?: 'photo' | 'video' | null;
}

interface KudozAction {
  label: string;
  earned: number;
  potential: number;
  total: number;
}

interface KudozBreakdown {
  earned: number;
  potential: number;
  total: number;
  actions: {
    baseReview: KudozAction;
    photos: KudozAction;
    videos: KudozAction;
    vibeRating: KudozAction;
    itemRatings: KudozAction;
    sharing: KudozAction;
  };
}

interface UseKudozBreakdownParams {
  hasVibeRating: boolean;
  mediaUploads: MediaUpload[];
  dishRatings: DishRating[];
  sharingSettings: SharingSettings;
  isFirstReview?: boolean;
  maxPhotos?: number;
  maxVideos?: number;
  maxItemRatings?: number;
}

/**
 * Hook to calculate Kudoz breakdown showing earned vs potential rewards
 * Follows adult-friendly emotional design principles with ambient awareness
 */
export function useKudozBreakdown({
  hasVibeRating,
  mediaUploads,
  dishRatings,
  sharingSettings,
  isFirstReview = false,
  maxPhotos = 5,
  maxVideos = 3,
  maxItemRatings = 10,
}: UseKudozBreakdownParams): KudozBreakdown {
  
  return useMemo(() => {
    // Photo Kudoz: +5 per photo.
    // `mediaUploads` = photos added in the general media section (file input / camera).
    // `dishRatings[].mediaUrl` = photos taken inside the dish-rating UI section.
    // These are separate React state arrays and never share entries — no deduplication
    // needed. The backend `media` array mirrors the same split (mediaUploads + filtered
    // dishRatings) so photo counts stay in sync end-to-end.
    const generalPhotoCount = mediaUploads.filter(m => m.type === 'photo').length;
    const dishPhotoCount = dishRatings.filter(d => d.mediaUrl && d.mediaType === 'photo').length;
    const photoCount = generalPhotoCount + dishPhotoCount;
    const photoEarned = photoCount * 5;
    const photoPotential = Math.max(0, (maxPhotos - photoCount) * 5);
    
    // Video Kudoz: +10 per video (same separation as photos above).
    const generalVideoCount = mediaUploads.filter(m => m.type === 'video').length;
    const dishVideoCount = dishRatings.filter(d => d.mediaUrl && d.mediaType === 'video').length;
    const videoCount = generalVideoCount + dishVideoCount;
    const videoEarned = videoCount * 10;
    const videoPotential = Math.max(0, (maxVideos - videoCount) * 10);
    
    // Vibe rating Kudoz: +5 if provided
    const vibeEarned = hasVibeRating ? 5 : 0;
    const vibePotential = hasVibeRating ? 0 : 5;
    
    // Item ratings Kudoz: +5 per rated item.
    // Counts ALL dishes the user gave a rating to (loved/fine/disliked), whether or
    // not they also added a dish photo. The backend mirrors this by reading
    // `dishRatings[].rating` from the POST body (not just media-backed items).
    const ratedItemsCount = dishRatings.filter(dish => dish.rating !== null).length;
    const itemRatingsEarned = ratedItemsCount * 5;
    const unratedItemsCount = Math.min(
      dishRatings.filter(dish => dish.rating === null).length,
      maxItemRatings - ratedItemsCount
    );
    const itemRatingsPotential = unratedItemsCount * 5;
    
    // Sharing Kudoz (additive as per spec)
    let sharingEarned = 0;
    let sharingPotential = 0;
    
    if (sharingSettings.mode === 'private') {
      // All sharing potential available
      sharingPotential = 35; // +5 friends + +10 public + +20 social
    } else if (sharingSettings.mode === 'friends') {
      sharingEarned = 5;
      sharingPotential = 30; // +10 public + +20 social
    } else if (sharingSettings.mode === 'public') {
      sharingEarned = 10; // +10 for public
      sharingPotential = 25; // +5 friends + +20 social
    } else if (sharingSettings.mode === 'social') {
      sharingEarned = 30; // +10 public + +20 social
      sharingPotential = 5; // +5 friends
    }
    
    // Base review: always +10 (always earned since they're creating a review)
    const baseReviewEarned = 10;

    // First review bonus: +25 if this is the user's first review
    const firstReviewBonus = isFirstReview ? 25 : 0;
    
    // Private reviews earn 0 Kudoz - only shared reviews earn rewards
    const isPrivate = sharingSettings.mode === 'private';
    const kudozMultiplier = isPrivate ? 0 : 1;
    
    // Calculate totals (apply 0x multiplier for private reviews)
    // Note: firstReviewBonus is included in the earned total (it has no "potential" equivalent)
    const totalEarned = (baseReviewEarned + firstReviewBonus + photoEarned + videoEarned + vibeEarned + itemRatingsEarned + sharingEarned) * kudozMultiplier;
    const totalPotential = photoPotential + videoPotential + vibePotential + itemRatingsPotential + sharingPotential;
    
    return {
      earned: totalEarned,
      potential: totalPotential,
      total: totalEarned + totalPotential,
      actions: {
        baseReview: {
          label: isFirstReview ? 'Creating review + First Review Bonus' : 'Creating review',
          earned: (baseReviewEarned + firstReviewBonus) * kudozMultiplier,
          potential: 0,
          total: (baseReviewEarned + firstReviewBonus) * kudozMultiplier,
        },
        photos: {
          label: 'Photos',
          earned: photoEarned * kudozMultiplier,
          potential: photoPotential,
          total: (photoEarned * kudozMultiplier) + photoPotential,
        },
        videos: {
          label: 'Videos',
          earned: videoEarned * kudozMultiplier,
          potential: videoPotential,
          total: (videoEarned * kudozMultiplier) + videoPotential,
        },
        vibeRating: {
          label: 'Experience rating',
          earned: vibeEarned * kudozMultiplier,
          potential: vibePotential,
          total: (vibeEarned * kudozMultiplier) + vibePotential,
        },
        itemRatings: {
          label: 'Item ratings',
          earned: itemRatingsEarned * kudozMultiplier,
          potential: itemRatingsPotential,
          total: (itemRatingsEarned * kudozMultiplier) + itemRatingsPotential,
        },
        sharing: {
          label: 'Sharing',
          earned: sharingEarned * kudozMultiplier,
          potential: sharingPotential,
          total: (sharingEarned * kudozMultiplier) + sharingPotential,
        },
      },
    };
  }, [hasVibeRating, mediaUploads, dishRatings, sharingSettings, isFirstReview, maxPhotos, maxVideos, maxItemRatings]);
}
